<template>
  <div class="checkout-screen">
    <div class="flex flex-col w-full flex-grow pb-32 lg:pb-0">
      <CheckoutHeader current-screen="checkout" />
      <main class="checkout-main">
        <div class="flex justify-center">
          <VPLoadingSpinner :loading="!formReady">
            <div class="lg:w-1/3 lg:min-w-[420px]">
              <CustomerInformationForm v-if="orderTotal === 0" />
              <StripePaymentForm
                v-else
                :quick-checkout-tos="event.config?.quickCheckoutTos"
                show-agreement-checkboxes
                :payment-intent="props.paymentIntent"
                :payment-intent-secret="props.paymentIntentSecret"
                :order-fingerprint="props.orderFingerprint"
              />
            </div>
          </VPLoadingSpinner>
        </div>
      </main>
    </div>
    <div class="cart shopping-cart overflow-x-auto lg:overflow-x-hidden">
      <VPLoadingSpinner :loading="!shoppingCartReady">
        <ShoppingCart
          read-only
          show-reset
          :event="event"
          :items="shoppingCartItems"
          :price-breakdown="priceBreakdown"
          :control-quantity="shoppingCart.controlQuantity"
          :mobile-next-label="$gettext('Show')"
          class="min-w-full"
        />
      </VPLoadingSpinner>
    </div>
  </div>
</template>

<script setup lang="ts">
import { useCheckoutStore } from '@/stores/checkout';
import { LoadingState } from '@/util/LoadingState';
import { computed, onMounted } from 'vue';
import StripePaymentForm from './StripePaymentForm.vue';
import CustomerInformationForm from './CustomerInformationForm.vue';
import ShoppingCart from '@/checkout/ShoppingCart.vue';
import { VPLoadingSpinner } from '@vp/vue-components';
import { until } from '@vueuse/core';
import { useRouter } from 'vue-router';
import { useToast } from 'vue-toastification';
import { useGettext } from 'vue3-gettext';
import { navigate } from 'vike/client/router';
import CheckoutHeader from '@/checkout/CheckoutHeader.vue';
import { useTracking } from '@/components/tracking/useTracking';
import { HARD_CODED_USD_CHANGE_ME } from '@/helpers/currency';

type Props = {
  paymentIntent?: string;
  paymentIntentSecret?: string;
  orderFingerprint?: string;
};

const props = defineProps<Props>();

const router = useRouter();
const toast = useToast();
const { $gettext } = useGettext();
const { trackEvent } = useTracking();

const checkoutStore = useCheckoutStore();
checkoutStore.init();

const event = computed(() => checkoutStore.getSelectedEvent);
const shoppingCart = computed(() => checkoutStore.shoppingCart);

const shoppingCartLoading = computed(() => checkoutStore.shoppingCartLoadingState);
const shoppingCartItems = computed(() => checkoutStore.getCartedTickets.filter(({ count }) => count > 0));
const priceBreakdown = computed(() => shoppingCart.value?.priceBreakdown);
const orderTotal = computed(() => priceBreakdown.value?.orderTotal || 0);

const shoppingCartReady = computed(() => shoppingCartLoading.value == LoadingState.DONE);
const formReady = computed(() => !!event.value && checkoutStore.getCartedTickets.length);

onMounted(async () => {
  await until(shoppingCartReady).toBe(true);
  // TODO: Fix. This is very broken
  if (!checkoutStore.hasValidLock) {
    // toast.error($gettext('You must lock tickets before payment!'));
    toast.error($gettext('Please click empty cart, refresh the page, and try again!'));
    navigate(router.resolve({ name: 'OrderNew' }).href);
  } else {
    trackEvent('begin_checkout', {
      curency: HARD_CODED_USD_CHANGE_ME,
      value: orderTotal.value,
      items: shoppingCartItems.value.map(({ id, count, name, breakdown }) => ({
        id,
        quantity: count,
        name,
        price: breakdown.basePrice,
      })),
    });
  }
});
</script>
